import { EyeInvisibleOutlined } from "@ant-design/icons";
import {
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from "@chakra-ui/react";
import MoreVert from "@material-ui/icons/MoreVert";
import { ChangeEvent, memo } from "react";
import {
  Column,
  ColumnInput,
  EnviromentTranslation,
  TabularForecastTranslation,
  Tresholds,
} from "../types";
import { ThresholdButton } from "./ThresholdButton";
import { ThresholdInput } from "./ThresholdInput";

interface TableHeaderProps {
  columns: Column[];
  translate: TabularForecastTranslation;
  environmentTranslation: EnviromentTranslation;
  isHideColumnDisabled?: boolean;
  tresholds: Tresholds;
  hidedColumns?: string[];
  hidedSubColumns?: string[];
  verifyIfColumnIsHided: (type: string | null) => boolean;
  isSubColumnHided: (type: string | null) => boolean;
  onHideColumn: (type: string) => void;
  onOpenManageColumnsModal: () => void;
  onHideSubCollumn: (type: string) => void;
  onToggleOperator: (key: string) => void;
  onTresholdInputChange: (
    e: ChangeEvent<HTMLInputElement>,
    key: string
  ) => void;
}

function TableHeaderComponent({
  columns,
  tresholds,
  verifyIfColumnIsHided,
  translate,
  environmentTranslation,
  isHideColumnDisabled,
  onHideColumn,
  onOpenManageColumnsModal,
  onToggleOperator,
  onTresholdInputChange,
  isSubColumnHided,
  onHideSubCollumn,
  hidedColumns,
  hidedSubColumns,
}: TableHeaderProps) {
  const getInputLabel = (input: ColumnInput) => {
    if (!input.label) return "";

    if (input.key === "dates") return translate("dates");

    return `${environmentTranslation(input.label)}`;
  };

  const calcColspan = (inputs: ColumnInput[]) => {
    const data = inputs.filter((item) => !isSubColumnHided(item.label));

    return data.length;
  };

  return (
    <Thead bg="#006699" position="sticky" top={0} zIndex={10}>
      <Tr>
        {columns.map((col, colIndex) => {
          const isDateCol = col.type === "date";
          const colspan = calcColspan(col.inputs);

          const haveEverySubColumnHided = col.inputs.every((input) =>
            isSubColumnHided(input.label)
          );

          return (
            !verifyIfColumnIsHided(col.type) &&
            !haveEverySubColumnHided && (
              <Th
                key={colIndex}
                colSpan={colspan || 1}
                color="white"
                textAlign="center"
                position={isDateCol ? "sticky" : "relative"}
                left={isDateCol ? 0 : "auto"}
                zIndex={isDateCol ? 100 : "10"}
                bg="#006699"
                border="none"
                borderLeft={colIndex !== 1 ? "1px solid black" : "none"}
                borderTop="1px solid black"
                borderBottom="1px solid black"
                _first={{
                  borderRight: "1px solid black",
                }}
                _last={{
                  borderRight: "1px solid black",
                }}
              >
                <Flex
                  gap={1}
                  justifyContent="center"
                  alignItems="center"
                  padding="4px 0"
                  position={isDateCol ? "sticky" : "relative"}
                  left={isDateCol ? 0 : "auto"}
                  zIndex={isDateCol ? 2 : "auto"}
                >
                  <span style={{ flex: 1 }}>
                    {col.label ? environmentTranslation(col.label) : ""}
                  </span>
                  {col.type && !isDateCol && (
                    <Menu>
                      <Tooltip label="Menu" hasArrow>
                        <MenuButton
                          as={Button}
                          variant="outline"
                          onFocus={(e) => e.preventDefault()}
                          border={0}
                          padding={0}
                          size="sm"
                          _active={{
                            bg: "transparent",
                            filter: "brightness(0.8)",
                          }}
                          _focus={{ bg: "transparent" }}
                          _hover={{
                            bg: "transparent",
                            filter: "brightness(0.8)",
                          }}
                        >
                          <MoreVert />
                        </MenuButton>
                      </Tooltip>{" "}
                      <MenuList>
                        <MenuItem
                          color="black"
                          display="flex"
                          alignItems="center"
                          gap={2}
                          isDisabled={isHideColumnDisabled}
                          onClick={() => {
                            col.type && onHideColumn(col.type);
                          }}
                        >
                          <EyeInvisibleOutlined style={{ fontSize: "16px" }} />
                          {translate("hideColumn")}
                        </MenuItem>
                        <MenuItem
                          color="black"
                          onClick={onOpenManageColumnsModal}
                        >
                          {translate("manageColumns")}
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  )}
                </Flex>
              </Th>
            )
          );
        })}
      </Tr>
      <Tr bg="white">
        {columns.flatMap((col) => {
          if (verifyIfColumnIsHided(col.type)) {
            return null;
          }

          return col.inputs
            .map((input, inputIndex) => {
              if (isSubColumnHided(input.label)) {
                return false;
              }

              const label = getInputLabel(input);
              const unitSymbol = input?.unitSymbol;

              const isDateCol = col.type === "date";

              const symbol = unitSymbol
                ? isDateCol
                  ? `${unitSymbol}`
                  : `(${unitSymbol})`
                : null;

              return (
                <Th
                  key={`${col.label}-${inputIndex}`}
                  color="#006699"
                  textAlign="center"
                  position={isDateCol ? "sticky" : "relative"}
                  left={isDateCol ? 0 : "auto"}
                  zIndex={isDateCol ? 100 : "auto"}
                  background={isDateCol ? "white" : "inherit"}
                  border="none"
                  borderRight="1px solid black"
                  borderBottom="1px solid black"
                  fontSize={"11px"}
                  px={2}
                  _first={{
                    borderLeft: "1px solid black",
                  }}
                >
                  <Text display="flex" flexDirection="column" as="p">
                    <span>{label}</span>
                    {unitSymbol && <span> {symbol}</span>}
                  </Text>
                  {!isDateCol && (
                    <Tooltip label={translate("hideColumn")}>
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => {
                          const isAllInputsInactive = col.inputs.map(
                            (colInput) => {
                              if (colInput.label === input.label) {
                                return true;
                              }

                              return isSubColumnHided(colInput.label);
                            }
                          );

                          if (isAllInputsInactive.every((item) => item)) {
                            onHideSubCollumn(input.label!);
                            onHideColumn(col.type!);
                          } else {
                            onHideSubCollumn(input.label!);
                          }
                        }}
                      >
                        <EyeInvisibleOutlined style={{ fontSize: "16px" }} />
                      </Button>
                    </Tooltip>
                  )}
                </Th>
              );
            })
            .filter((item) => !!item);
        })}
      </Tr>
      <Tr bg="white">
        {columns.flatMap((col) => {
          if (verifyIfColumnIsHided(col.type)) {
            return null;
          }

          const isDateCol = col.type === "date";

          return col.inputs
            .map((input, inputIndex) => {
              if (isSubColumnHided(input.label)) {
                return false;
              }

              return input.showThresholds ? (
                <Th
                  key={`${col.label}-${inputIndex}-input`}
                  textAlign="center"
                  padding="4px 6px"
                  border="none"
                  borderRight="1px solid black"
                  borderBottom="1px solid black"
                >
                  <Stack flexDir="row" gap={1} alignItems="center" spacing={0}>
                    <ThresholdButton
                      operator={tresholds[input.key!]?.operator || ">"}
                      onClick={() => onToggleOperator(input.key!)}
                      tooltipLabel={
                        tresholds[input.key!]?.operator === "<"
                          ? translate("tableTresholdTooltipLessThan")
                          : translate("tableTresholdTooltipGreaterThan")
                      }
                    />
                    <ThresholdInput
                      autoFocus={
                        col.type === "weather" && input.label === "wind_vel"
                      }
                      label={translate("thresholdTooltip")}
                      dataKey={input.key}
                      title={translate("thresholdTitle") || ""}
                      onChange={onTresholdInputChange}
                      value={tresholds[input.key!]?.value || ""}
                    />
                  </Stack>
                </Th>
              ) : (
                <Th
                  key={`${col.label}-${inputIndex}-empty`}
                  position={isDateCol ? "sticky" : "relative"}
                  left={isDateCol ? 0 : "auto"}
                  zIndex={isDateCol ? 2 : "auto"}
                  background={isDateCol ? "white" : "inherit"}
                  border="none"
                  borderLeft="1px solid black"
                  borderRight="1px solid black"
                  borderBottom="1px solid black"
                  textAlign="center"
                  color="#006699"
                >
                  {isDateCol && translate("thresholdTitle")}
                </Th>
              );
            })
            .filter((item) => !!item);
        })}
      </Tr>
    </Thead>
  );
}

const areEqual = (prev: string[], next: string[]) => {
  return !(prev.length !== next.length);
};

const areThresholdEqual = (prevFilters: Tresholds, nextFilters: Tresholds) => {
  const prevKeys = Object.keys(prevFilters);
  const nextKeys = Object.keys(nextFilters);

  if (prevKeys.length !== nextKeys.length) {
    return false;
  }

  return prevKeys.every((key) => {
    const prev = prevFilters[key];
    const next = nextFilters[key];

    return (
      next !== undefined &&
      prev.value === next.value &&
      prev.operator === next.operator
    );
  });
};

export const TableHeader = memo(
  TableHeaderComponent,
  (prevProps, nextProps) => {
    const changedSubcolumns = areEqual(
      prevProps.hidedSubColumns!,
      nextProps.hidedSubColumns!
    );
    const changedColumns = areEqual(
      prevProps.hidedColumns!,
      nextProps.hidedColumns!
    );
    const changeTresholds = areThresholdEqual(
      prevProps.tresholds,
      nextProps.tresholds
    );

    if (!changeTresholds) return false;
    if (nextProps.columns.length !== prevProps.columns.length) return false;
    if (!changedSubcolumns || !changedColumns) return false;

    return true;
  }
);
